/**
 * Cart Bundle Remove - Intercepts remove clicks for bundle items
 * Must run BEFORE theme's cart handlers
 */

(function() {
  'use strict';

  let isProcessing = false;

  /**
   * Find cart item element by line index
   */
  function findCartItem(lineIndex) {
    return document.querySelector(`#CartItem-${lineIndex}, #CartDrawerItem-${lineIndex}`);
  }

  /**
   * Find all bundle items with same Set ID
   */
  function findBundleItems(setId) {
    const items = [];
    document.querySelectorAll(`[data-bundle-set-id="${setId}"]`).forEach(el => {
      const line = el.dataset.cartLine;
      if (line) {
        items.push({ element: el, line: parseInt(line) });
      }
    });
    return items;
  }

  /**
   * Get current cart state from Shopify
   */
  async function getCurrentCart() {
    const response = await fetch(`${theme.routes.cart_url}.js`);
    if (!response.ok) {
      throw new Error(`Failed to get cart: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Remove bundle (all items with same Set ID)
   */
  async function removeBundle(bundleItems) {
    // Show loading
    bundleItems.forEach(item => {
      item.element.classList.add('loading', 'thb-loading');
    });

    try {
      console.log('Fetching current cart state...');
      
      // Get current cart to find actual keys/IDs
      const cart = await getCurrentCart();
      
      // Build array of variant keys to remove based on Set ID
      const setId = bundleItems[0].element.dataset.bundleSetId;
      const itemsToRemove = [];
      
      cart.items.forEach((item, index) => {
        if (item.properties && item.properties['_Set ID'] === setId) {
          itemsToRemove.push({
            key: item.key,
            line: index + 1,
            id: item.id,
            variant_id: item.variant_id
          });
        }
      });

      console.log('Items to remove:', itemsToRemove);

      if (itemsToRemove.length === 0) {
        throw new Error('No matching items found in cart');
      }

      // Method 1: Try batch update with keys
      const updates = {};
      itemsToRemove.forEach(item => {
        updates[item.key] = 0;
      });

      console.log('Sending updates:', updates);

      const response = await fetch(`${theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          updates: updates
        })
      });

      if (!response.ok) {
        // If batch update fails, try removing one by one
        console.log('Batch update failed, trying sequential removal...');
        return await removeItemsSequentially(itemsToRemove);
      }

      const updatedCart = await response.json();

      // Trigger refresh
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));

      console.log('✓ Bundle removed successfully');
      return updatedCart;

    } catch (error) {
      console.error('Bundle remove error:', error);
      
      // Remove loading state
      bundleItems.forEach(item => {
        item.element.classList.remove('loading', 'thb-loading');
      });
      
      throw error;
    }
  }

  /**
   * Remove items one by one (fallback method)
   */
  async function removeItemsSequentially(items) {
    console.log('Removing items sequentially...');
    
    // Sort by line number descending (remove from bottom to top)
    // This prevents line number shifting issues
    items.sort((a, b) => b.line - a.line);
    
    for (const item of items) {
      try {
        console.log(`Removing line ${item.line}...`);
        
        const response = await fetch(`${theme.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            line: item.line,
            quantity: 0
          })
        });

        if (!response.ok) {
          console.error(`Failed to remove line ${item.line}: ${response.status}`);
        }
        
        await response.json();
      } catch (error) {
        console.error(`Error removing line ${item.line}:`, error);
      }
    }

    // Trigger refresh after all removals
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }));

    console.log('✓ Sequential removal complete');
  }

  /**
   * Intercept ALL click events on document
   * Run in CAPTURE phase before any other handlers
   */
  function interceptRemoveClicks(event) {
    // Find if click is on or inside a remove button
    const removeButton = event.target.closest('.remove');
    
    if (!removeButton || isProcessing) {
      return; // Not a remove button or already processing
    }

    const lineIndex = removeButton.dataset.index;
    if (!lineIndex) {
      return; // No line index
    }

    // Find the cart item
    const cartItem = findCartItem(lineIndex);
    if (!cartItem) {
      console.log('Cart item not found:', lineIndex);
      return; // Not found, let default handler work
    }

    // Check if this is a bundle item
    const bundleSetId = cartItem.dataset.bundleSetId;
    if (!bundleSetId) {
      console.log('Not a bundle item, allowing default removal');
      return; // Not a bundle, let default handler work
    }

    // This IS a bundle item - intercept!
    console.log('✓ Bundle item detected:', bundleSetId);

    // Find all items in bundle
    const bundleItems = findBundleItems(bundleSetId);
    console.log('✓ Found bundle items:', bundleItems.length);

    if (bundleItems.length <= 1) {
      console.log('Only 1 item in bundle, allowing default removal');
      return; // Only one item, treat as normal
    }

    // STOP the event from reaching theme handlers
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Process bundle removal
    isProcessing = true;
    console.log('→ Removing bundle...');

    removeBundle(bundleItems)
      .catch(err => {
        console.error('✗ Failed to remove bundle:', err);
        alert('Error removing bundle items. Please refresh the page and try again.');
      })
      .finally(() => {
        isProcessing = false;
      });

    return false;
  }

  /**
   * Initialize - attach to document in CAPTURE phase
   * This runs BEFORE theme's event listeners
   */
  function init() {
    // Use capture phase (3rd parameter = true)
    // This ensures we intercept BEFORE theme handlers
    document.addEventListener('click', interceptRemoveClicks, true);
    console.log('✓ Cart Bundle Remove initialized (capture phase)');
  }

  // Run immediately if DOM ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
